import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { providerReviewRepository } from "../repositories";
export const useProviderReviewStore = defineStore("providerReviews", () => {
    // STATE
    const reviews = ref([]);
    const isLoadingReviews = ref(false);
    const reviewsError = ref(null);
    const _totalReviews = ref(0);
    const _averageRating = ref(0);
    const _reviewsThisMonth = ref(0);
    const platformTotal = ref(0);
    // GETTERS
    const totalReviews = computed(() => _totalReviews.value);
    const averageRating = computed(() => _averageRating.value.toFixed(1));
    const reviewsThisMonth = computed(() => _reviewsThisMonth.value);
    // ACTIONS
    async function fetchReviews(scope = "provider") {
        isLoadingReviews.value = true;
        reviewsError.value = null;
        try {
            const data = await providerReviewRepository.getReviews(scope);
            reviews.value = data.reviews;
            _totalReviews.value = data.stats.totalReviews;
            _averageRating.value = data.stats.averageRating ?? 0;
            _reviewsThisMonth.value = data.stats.thisMonth;
        }
        catch (err) {
            reviewsError.value =
                err instanceof Error ? err.message : "Error fetching reviews";
        }
        finally {
            isLoadingReviews.value = false;
        }
    }
    async function fetchPlatformReviews(filters = {}) {
        isLoadingReviews.value = true;
        reviewsError.value = null;
        try {
            const data = await providerReviewRepository.getPlatformReviews(filters);
            reviews.value = data.items;
            platformTotal.value = data.total;
            _totalReviews.value = data.total;
            _averageRating.value =
                data.items.length > 0
                    ? data.items.reduce((sum, review) => sum + review.rating, 0) /
                        data.items.length
                    : 0;
            _reviewsThisMonth.value = data.items.filter((review) => {
                const submittedAt = new Date(review.date);
                const now = new Date();
                return (submittedAt.getUTCFullYear() === now.getUTCFullYear() &&
                    submittedAt.getUTCMonth() === now.getUTCMonth());
            }).length;
        }
        catch (err) {
            reviewsError.value =
                err instanceof Error ? err.message : "Error fetching reviews";
        }
        finally {
            isLoadingReviews.value = false;
        }
    }
    async function saveProviderReply(reviewId, body) {
        const review = reviews.value.find((item) => resolveReviewId(item) === reviewId);
        const reply = review?.provider_reply
            ? await providerReviewRepository.updateProviderReply(reviewId, body)
            : await providerReviewRepository.createProviderReply(reviewId, body);
        if (review)
            review.provider_reply = reply;
    }
    async function deleteProviderReply(reviewId) {
        await providerReviewRepository.deleteProviderReply(reviewId);
        const review = reviews.value.find((item) => resolveReviewId(item) === reviewId);
        if (review)
            review.provider_reply = null;
    }
    async function moderateReview(reviewId, action, reason) {
        await providerReviewRepository.moderateReview(reviewId, action, reason);
        const review = reviews.value.find((item) => resolveReviewId(item) === reviewId);
        if (review) {
            review.moderation_status = action === "hide" ? "hidden" : "published";
        }
    }
    async function moderateReply(replyId, action, reason) {
        const reply = await providerReviewRepository.moderateReply(replyId, action, reason);
        const review = reviews.value.find((item) => item.provider_reply?.id === replyId);
        if (review)
            review.provider_reply = reply;
    }
    function $reset() {
        reviews.value = [];
        isLoadingReviews.value = false;
        reviewsError.value = null;
        _totalReviews.value = 0;
        _averageRating.value = 0;
        _reviewsThisMonth.value = 0;
        platformTotal.value = 0;
    }
    function resolveReviewId(review) {
        return review.review_id ?? review.id ?? 0;
    }
    return {
        reviews,
        isLoadingReviews,
        reviewsError,
        totalReviews,
        averageRating,
        reviewsThisMonth,
        platformTotal,
        fetchReviews,
        fetchPlatformReviews,
        saveProviderReply,
        deleteProviderReply,
        moderateReview,
        moderateReply,
        $reset,
    };
});
